import { Card, Typography, message } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import {
  DELETE_PRODUCTION_DETAILS,
  EXPORT_PRODUCTION_DETAILS,
  GET_PRODUCTION_DETAILS,
  SUPPLIER_WISE_ITEM_PRICE_REPORT,
  VIEW_PRODUCTION_DETAILS,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ListTable from "../../../common/ListTable";
import CustomFilter from "../../Common/CustomFilter";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";
import FilterComponent from "../../Configuration/CommonComponents/FilterComponent";
import { cglFormatDate } from "utils/functions";

const SupplierwiseItemPriceReport = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fix round 1: guard against setState after unmount (cold review finding 2, applied here
  // too since this report's unfiltered mount fetch returns a large dataset — 1303 pages at
  // 10/page in testing — and can plausibly be slow).
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // Final fix wave (C1): request-sequence ref — a fast manual Preview must never be
  // overwritten by a slower, stale mount request resolving afterwards.
  const reqIdRef = useRef(0);
  const lastFilterValuesRef = useRef(null);
  const [fetchError, setFetchError] = useState(null);

  // Antd
  const { Title } = Typography;

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const cutPath = (path) => {
    const updatedPath = path ? path.split("public/production_details/")[1] : "";
    return updatedPath;
  };

  const getAllDetails = useCallback(async (filterValues) => {
    setLoading(true);
    const query = `${GET_PRODUCTION_DETAILS}`;
    const bodyData = {
      per_page: filterValues?.per_page || 10,
      ...filterValues,
    };

    const response = await getData(query, false, bodyData);

    if (response?.status === 200) {
      setLoading(false);
      setTableData(response?.data?.data);
    } else {
      setLoading(false);
      message.error("Error getting details data");
    }
  }, []);

  const exportDetails = async (filterValues) => {
    const { good_description, item_category_ids, item_ids, supplier_id } = filterValues;
    const bodyData = {
      ...(good_description && { good_description }),
      ...(item_category_ids && { item_category_ids }),
      ...(item_ids && { item_ids }),
      ...(supplier_id && { supplier_id }),
    };
    // if (Object.values(bodyData).length && !Object.values(bodyData).some((val) => val === undefined)) {
      setLoading(true);

      const query = `${SUPPLIER_WISE_ITEM_PRICE_REPORT}`;

      const response = await getData(query, false, bodyData, { "Content-Type": "blob" }, "arraybuffer");

      console.log(response)

      if (response && response?.data) {
        // Download the file
        const outputFilename = `supplier_wise_item_price_report.xlsx`;

        // If you want to download file automatically using link attribute.
        const url = URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        setLoading(false);
        message.success("Fabric Exported Successfully");
        refetch();
      } else {
        setLoading(false);
        message.error("Something went wrong");
      }
    // }
  };

  const previewFunction = async (filterValues) => {
    // Final fix wave (C1): claim this request's sequence number before the await so a
    // later, faster request (e.g. the user's own manual Preview) can invalidate this one.
    const myReq = ++reqIdRef.current;
    lastFilterValuesRef.current = filterValues;
    setFetchError(null);
    setLoading(true);

    const bodyData = {
      ...filterValues,
      report_type: "preview",
    };

    const query = `${SUPPLIER_WISE_ITEM_PRICE_REPORT}`;
    const response = await getData(query, false, bodyData);

    // Fix round 1: mounted guard (cold review finding 2).
    // Final fix wave (C1): also drop this response if a newer request has since been fired
    // (stale-response race — see FINAL-REVIEW.md).
    if (!mountedRef.current || myReq !== reqIdRef.current) return;

    if (response?.data?.code === 200) {
      setLoading(false);
      setTableData(response?.data?.data);
    } else {
      setFetchError("Could not load this list.");
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  // Effects
  // No date field on this report (payload has no date param); confirmed via a direct API
  // call that the endpoint returns rows with no supplier filter, so load unfiltered on mount
  // (same request an untouched Preview click sends).
  useEffect(() => {
    previewFunction({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const openFile = (name) => {
    const URL = `${process.env.REACT_APP_BASE_URL}/storage/production_details/${name}`;
    window.open(URL, "_blank", "noreferrer");
  };

  const tableColumns = [
    {
      title: "Style Name",
      dataIndex: "style_name",
      key: "style_name",
      width: 150,
      fixed: "left",
    },
    {
      title: "PI Number",
      dataIndex: "pi_number",
      key: "pi_number",
      width: 200,
      fixed: "left",
    },
    {
      title: "Goods Description",
      dataIndex: "goods_description",
      key: "goods_description",
      width: 150,
    },
    {
      title: "Supplier Name",
      dataIndex: "supplier_name",
      key: "supplier_name",
      width: 200,
    },
    {
      title: "Category",
      dataIndex: "category_name",
      key: "category_name",
      width: 150,
    },
    {
      title: "Item Name",
      dataIndex: "item_name",
      key: "item_name",
      width: 250,
    },
    {
      title: "Unit",
      dataIndex: "unit_name",
      key: "unit_name",
      width: 80,
      align: "center",
    },
    {
      title: "PI Qty",
      dataIndex: "pi_qty",
      key: "pi_qty",
      width: 120,
      align: "right",
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 100,
      align: "right",
      // render: (value) => parseFloat(value).toFixed(4),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right",
      // render: (value) => parseFloat(value).toFixed(4),
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={exportDetails}
        filterName={"supplier_wise_item_price_report"}
        searchButtonText={"Export"}
        defaultOpen
        setShowFilter={setShowFilter}
        showFilter={showFilter}
        isPreview={true}
        previewFunction={previewFunction}
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={exportDetails}
                hideAddButton={true}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Supplier-wise Item Price Report</Title>}
          />
        }
        content={
          <>
            <div className="containt-body">
              <Card>{memoizedCustomFilter}</Card>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => previewFunction(lastFilterValuesRef.current)}
                dataSource={tableData}
                columns={tableColumns.map((col, idx) => ({
                  ...col,
                  fixed: idx < 5 ? "left" : undefined,
                }))}
                pagination
                scroll={{
                  x: 1200,
                  y: 500,
                }}
                bordered
                sticky
                // Final fix wave (M3): "to show yet" implies something is still loading;
                // the house copy for an empty open-orders filter is more specific.
                locale={{ emptyText: "No open orders match this filter." }}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default SupplierwiseItemPriceReport;
